import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type SidebarData, type NavGroup, type NavItem } from '../types';
import { sidebarDataBase } from './sidebar-data';
import { SidebarContext } from './sidebar-context-def';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translateTitle = (t: any, item: NavItem): NavItem => {
  const newItem = { ...item };
  if (newItem.title) {
    newItem.title = t(newItem.title);
  }
  if ('items' in newItem && newItem.items) {
    newItem.items = newItem.items.map(subItem => ({
      ...subItem,
      title: t(subItem.title)
    }));
  }
  return newItem;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translateNavGroup = (t: any, group: NavGroup): NavGroup => {
  return {
    ...group,
    title: group.title ? t(group.title) : "",
    items: group.items.map(item => translateTitle(t, item))
  };
};

export const SidebarDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [sidebarData, setSidebarData] = useState<SidebarData>({ navGroups: [] });

  useEffect(() => {
    // Function to update sidebar data with translations
    const updateSidebarData = () => {
      const translatedData: SidebarData = {
        navGroups: sidebarDataBase.navGroups.map(group => translateNavGroup(t, group))
      };
      setSidebarData(translatedData);
    };

    // Update when i18n is initialized and on language change
    if (i18n.isInitialized) {
      updateSidebarData();
    }

    const handleLanguageChanged = () => {
      updateSidebarData();
    };

    // Listen for i18n initialization and language changes
    i18n.on('initialized', updateSidebarData);
    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('initialized', updateSidebarData);
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [t, i18n]);

  return (
    <SidebarContext.Provider value={{ sidebarData }}>
      {children}
    </SidebarContext.Provider>
  );
};
